import React, { createContext, useState, useEffect, useCallback } from "react";
import { getProfileMe, logoutApi } from "../service/authService";
import { clearCompanyDocumentsAck, normalizeProfileMe } from "../service/profileService";
import {
  getSpecialistProfileMe,
  getStoredUserRole,
  setStoredUserRole,
  clearStoredUserRole,
  clearSpecialistDocumentsAck,
  normalizeSpecialistProfile,
  USER_ROLE,
} from "../service/specialistService";
import { useLanguage } from "./LanguageContext";
import LogoutConfirmModal from "../components/LogoutConfirmModal/LogoutConfirmModal";

export const AuthContext = createContext();

const persistUser = (userData) => {
  if (!userData) return;
  localStorage.setItem("userData", JSON.stringify(userData));
};

const AuthContextProvider = ({ children }) => {
  const { language } = useLanguage();
  const tokenExists = !!localStorage.getItem("token");
  const savedUser = (() => {
    try {
      const raw = localStorage.getItem("userData");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();
  const [user, setUser] = useState(savedUser);
  const [userRole, setUserRoleState] = useState(getStoredUserRole());
  const [isAuthenticated, setIsAuthenticated] = useState(tokenExists);
  const [loading, setLoading] = useState(true);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);

  const setUserRole = (role) => {
    const nextRole = role === USER_ROLE.SPECIALIST ? USER_ROLE.SPECIALIST : USER_ROLE.USER;
    setStoredUserRole(nextRole);
    setUserRoleState(nextRole);
  };

  const getUser = useCallback(async () => {
    const token = localStorage.getItem("token");
    const role = getStoredUserRole();
    setUserRoleState(role);

    if (token) {
      try {
        if (role === USER_ROLE.SPECIALIST) {
          try {
            const response = await getSpecialistProfileMe();
            const specialist = normalizeSpecialistProfile(response);
            const userData = specialist
              ? { ...specialist, name: specialist.full_name }
              : normalizeProfileMe(response);
            setUser(userData);
            persistUser(userData);
            setIsAuthenticated(true);
          } catch (specialistError) {
            if (specialistError?.response?.status === 401) throw specialistError;
            let fallback = null;
            try {
              const raw = localStorage.getItem("userData");
              fallback = raw ? JSON.parse(raw) : null;
            } catch {
              fallback = null;
            }
            const nextUser = fallback
              ? { ...fallback, name: fallback.full_name || fallback.name }
              : fallback;
            setUser(nextUser);
            setIsAuthenticated(Boolean(nextUser));
          }
        } else {
          const response = await getProfileMe();
          const userData = normalizeProfileMe(response);
          setUser(userData);
          persistUser(userData);
          setIsAuthenticated(true);
        }
      } catch (e) {
        console.error("User authentication failed", e);
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setLoading(false);
      }
    } else {
      setIsAuthenticated(false);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getUser();
  }, [getUser, isAuthenticated, language]);

  const login = (token, userData) => {
    localStorage.setItem("token", token);
    setIsAuthenticated(true);
    setUser(normalizeProfileMe(userData) ?? userData);
  };

  const logout = () => {
    setLogoutConfirmOpen(true);
  };

  const closeLogoutConfirm = () => {
    if (logoutLoading) return;
    setLogoutConfirmOpen(false);
  };

  const confirmLogout = async () => {
    if (logoutLoading) return;
    setLogoutLoading(true);
    try {
      await logoutApi();
    } catch (e) {
      console.error("Logout API error", e);
    } finally {
      localStorage.removeItem("token");
      localStorage.removeItem("userData");
      localStorage.removeItem("userDataLast");
      clearCompanyDocumentsAck();
      clearSpecialistDocumentsAck();
      clearStoredUserRole();
      setUserRoleState(USER_ROLE.USER);
      setIsAuthenticated(false);
      setUser(null);
      window.location.href = "/";
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        userRole,
        setUserRole,
        isAuthenticated,
        setIsAuthenticated,
        login,
        logout,
        getUser,
        loading,
      }}
    >
      {children}
      <LogoutConfirmModal
        isOpen={logoutConfirmOpen}
        onClose={closeLogoutConfirm}
        onConfirm={confirmLogout}
        loading={logoutLoading}
      />
    </AuthContext.Provider>
  );
};

export default AuthContextProvider;
